import React, { useMemo } from 'react'
import { Chip, IconClose, phoneFormatter } from '@dostavkee/web-ui'
import { useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'

import { useRecruiterOptionsFetch } from '@/entities/recruiter'

import type { RecruitmentSearchParams } from '../../model/recruitment-search-params'
import { useRecruitmentSearch } from '../../model/use-recruitment-search'
import styles from './recruitment-search-chips.module.scss'

interface SearchChip {
    key: string
    value: string
    label: string
}

const omit = <T extends Omit<RecruitmentSearchParams, 'page'>, K extends keyof T>(
    object: T,
    keys: K[]
): Omit<T, K> => {
    const result: Partial<T> = {
        ...object,
    }

    for (const key of keys) {
        delete result[key]
    }

    return result as Omit<T, K>
}

export const RecruitmentSearchChips = () => {
    const { searchValues } = useRecruitmentSearch()
    const navigate = useNavigate()
    const { recruiterOptions } = useRecruiterOptionsFetch()
    const chipsValues = useMemo(() => {
        const values: SearchChip[] = []

        if (searchValues?.phone) {
            values.push({
                key: 'phone',
                value: searchValues.phone,
                label: phoneFormatter(searchValues.phone),
            })
        }

        if (searchValues?.from_date && searchValues?.to_date) {
            values.push({
                key: 'date_range',
                value: `${searchValues.from_date} - ${searchValues.to_date}`,
                label: `${dayjs(searchValues.from_date, 'DD-MM-YYYY').format('DD.MM.YYYY')} - ${dayjs(searchValues.to_date, 'DD-MM-YYYY').format('DD.MM.YYYY')}`,
            })
        }

        if (searchValues.recruiter_id) {
            values.push({
                key: 'recruiter_id',
                value: searchValues.recruiter_id,
                label:
                    recruiterOptions?.find((option) => option.id === searchValues.recruiter_id)
                        ?.name ?? '',
            })
        }

        return values
    }, [searchValues, recruiterOptions])

    const handleClickChip = (searchField: string) => {
        switch (searchField) {
            case 'phone': {
                navigate({
                    to: '/recruitment',
                    search: {
                        ...omit(searchValues, ['phone']),
                        page: 1,
                    },
                })
                break
            }
            case 'date_range': {
                navigate({
                    to: '/recruitment',
                    search: {
                        ...omit(searchValues, ['from_date', 'to_date']),
                        page: 1,
                    },
                })
                break
            }
            case 'recruiter_id': {
                navigate({
                    to: '/recruitment',
                    search: {
                        ...omit(searchValues, ['recruiter_id']),
                        page: 1,
                    },
                })
                break
            }
            default: {
                break
            }
        }
    }

    return (
        <div className={styles['recruitment-search-chips']}>
            {chipsValues.map(({ key, label }, index) => (
                <Chip
                    key={index}
                    postfixEl={<IconClose color='var(--text-and-icon-on-color)' size={20} />}
                    size='m'
                    variant='primary'
                    onClick={() => handleClickChip(key)}
                >
                    {label}
                </Chip>
            ))}
        </div>
    )
}
